import React, { useEffect, useState } from 'react';
import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as CarChair } from 'icons/car-chair-icon.svg';
import { ReactComponent as CarIcon } from 'icons/car-filled-logo.svg';
import { ReactComponent as PointsIcon } from 'icons/points-icon.svg';
import { Button, InputField, SelectFieldAdditionalItem, TableWrapper } from 'components/Global';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'components/Context/AppContext';
import { checkEmptyObject, checkPermission, setCurrency } from 'utils/functionality';
import countryCodes from 'utils/country-codes.json';
import { getCustomerData } from 'features/user/slice';
import { fetchOrderById, fetchSummaryOrder } from 'features/orders/actions';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import SelectFieldCar from 'components/Global/SelectFieldCar';
import InputAdditionals from 'components/Global/InputAdditionals';
import { getVehiclesByFilter } from 'features/vehicle/action';
import SelectFieldShuttle from 'components/Global/SelectFieldShuttle';
import { getAllDeliveryLocations } from 'features/delivery-location/actions';
import { getServices } from 'features/services/actions';
import SelectFieldDate from 'components/Global/SelectFieldDate';
import { addDays, format, parseISO, subDays, subHours } from 'date-fns';
import SelectFieldTime from 'components/Global/SelectFieldTime';
import { rentalLocation } from 'features/rental-location/slice';
import { getApprovalSettings, updateApprovalOrder } from 'features/approvals/actions';
import { getRentalLocationById } from 'features/rental-location/actions';
import InputViolation from 'components/Global/InputViolation';
import moment from 'moment-timezone';
import { getAllAddonByParams, getOrderAddon } from 'features/addon/actions';
import { overtimeWithoutDriver } from '../CustomOrderComponent/DetailSewa/WithoutDriverForm';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import { trackEvent } from 'utils/analytics';

const INITIAL_STATE = {
  booking_price: 0,
  currency: '',
  order_detail: {
    passenger_number: '',
    baggage: '',
    end_booking_date: '',
    end_booking_time: '',
    rental_delivery_location: '',
    rental_delivery_location_detail: '',
    rental_return_location: '',
    rental_return_location_detail: '',
    start_booking_date: '',
    start_booking_time: '',
    vehicle_id: 0,
    location_id: 0,
    loc_time_id: '',
    landing_time: '',
    flight_number: '',
  },
  order_type_id: 0,
  deposit: 0,
  over_time_price: 0,
  over_time: 0,
};

const EditOrder = ({ transactionKey }) => {
  const { setShowConfirmation, showToast, setShowSpinner } = useAppContext();

  const [selectedCountry, setSelectedCountry] = useState({
    name: '',
    code: '',
  });
  const [orderData, setOrderData] = useState(INITIAL_STATE);
  const [selectedOvertime, setSelectedOvertime] = useState('');
  const [selectedCar, setSelectedCar] = useState(undefined);
  const [vehicleInput, setVehicleInput] = useState('');
  // ADDITIONAL STATE
  const [additionalList, setAdditionalList] = useState([]);
  const [additionalPrice, setAdditionalPrice] = useState(0);
  // VIOLIATION STATE
  const [violationList, setViolationList] = useState([]);
  const [violationPrice, setViolationPrice] = useState(0);
  const [deliveryLocation, setDeliveryLocation] = useState({
    id: 0,
    name: '',
  });
  const [returnLocation, setReturnLocation] = useState({
    id: 0,
    name: '',
  });
  const [deliveryLocationDetail, setDeliveryLocationDetail] = useState('');
  const [returnLocationDetail, setReturnLocationDetail] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [startRentDate, setStartRentDate] = useState('');
  const [endRentDate, setEndRentDate] = useState('');
  const [baggage, setBaggage] = useState(0);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [selectedAdditionalItem, setSelectedAdditionalItem] = useState(undefined);
  const [additionalSearchInput, setAdditionalSearchInput] = useState('');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const customer = useSelector(getCustomerData);
  const { data: vehicleList } = useSelector((state) => state.vehicle);
  const { data: rentalServicesData } = useSelector((state) => state.services);
  const { data: dataDeliveryLocation } = useSelector((state) => state.deliveryLocation);
  const { data: summaryData, status: summaryStatus } = useSelector((state) => state.summaryOrder);
  const approvalData = useSelector((state) => state.approvals.approvalSetting);
  const { data, status } = useSelector((state) => state.detailOrder);
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: additionalData } = useSelector((state) => state.addon);
  const { data: orderAddons } = useSelector((state) => state.orderAddon);
  const user = useSelector((state) => state.user.profile);

  const isImprovUpdateDataEnabled = useFliptBoolean(
    'FLAG_FE_IMPROVEMENT_UPDATE_DATA_ORDER',
    false,
    user.id || 'anonymous',
  );
  const isDriverDeleteApproval = useFliptBoolean('FLAG_FE_DELETE_DRIVER_APPROVAL', false, user.id || 'anonymous');

  const [searchParams] = useSearchParams();

  const findCountryByCode = (code) => countryCodes.find((item) => item.dial_code == code);

  const isUpdate = searchParams.get('update');

  const getTotalPrice = (total, point, voucher) => {
    let finalPrice = total - point;
    const voucherValue = voucher?.length > 0 ? voucher[0].value : undefined;
    if (voucherValue) {
      finalPrice -= voucherValue;
    }
    return isNaN(finalPrice) ? 0 : finalPrice;
  };

  useEffect(() => {
    if (selectedCar) {
      setIsPageLoaded(true);
    }
  }, [selectedCar]);

  useEffect(() => {
    if (!isUpdate) return;
    if (checkEmptyObject(data)) return;
    const approvalKey = data.is_admin_creation ? 'update-order-to-higher-level' : 'update-order-to-customer';
    dispatch(getApprovalSettings({ limit: 10, key: approvalKey }));
    dispatch(getAllDeliveryLocations({ currentPage: 1, limit: 30, id: data.order_detail.location_id }));
    dispatch(getRentalLocationById(data.order_detail.location_id));
    dispatch(getServices());
  }, [isUpdate, data]);

  useEffect(() => {
    dispatch(fetchOrderById(transactionKey));
    dispatch(getOrderAddon(transactionKey));
  }, [transactionKey]);

  useEffect(() => {
    if (customer) {
      setSelectedCountry(findCountryByCode(data?.is_admin_creation ? data?.phone_country_code : customer?.phone_code));
    }
  }, [customer, data]);

  useEffect(() => {
    if (status === 'loading' || summaryStatus === 'loading') {
      setShowSpinner(true);
    } else {
      setShowSpinner(false);
    }
  }, [status, summaryStatus]);

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(data, 'order_additional_fees')) {
      if (data.order_additional_fees !== null) {
        setAdditionalList(data.order_additional_fees);
        setAdditionalPrice(0);
      }
    }
  }, [data]);

  useEffect(() => {
    if (additionalList.length > 0) {
      setAdditionalPrice(() => additionalList.map((item) => item.value).reduce((acc, cVal) => acc + cVal, 0));
      return;
    }
    setAdditionalPrice(0);
  }, [additionalList]);

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(data, 'order_violations')) {
      if (data.order_violations !== null) {
        setViolationList(data.order_violations);
        setViolationPrice(0);
      }
    }
  }, [data]);

  useEffect(() => {
    if (violationList.length > 0) {
      setViolationPrice(() => violationList?.map((item) => item.cost).reduce((acc, cVal) => acc + cVal, 0));
      return;
    }
    setViolationPrice(0);
  }, [violationList]);

  useEffect(() => {
    if (checkEmptyObject(data) || isPageLoaded) return;
    setSelectedCar(data.order_detail.vehicle);
  }, [data, isPageLoaded]);

  const backHandler = () => {
    navigate(-1);
  };

  const updateHandler = () => {
    if (
      orderData.order_detail.end_booking_date === '' ||
      orderData.order_detail.end_booking_time === '' ||
      orderData.order_detail.start_booking_date === '' ||
      orderData.order_detail.start_booking_time === '' ||
      isNaN(baggage) ||
      baggage === '' ||
      isNaN(orderData.order_detail.passenger_number) ||
      orderData.order_detail.passenger_number === '' ||
      deliveryLocation.name === '' ||
      returnLocation.name === '' ||
      deliveryLocationDetail === '' ||
      returnLocationDetail === ''
    ) {
      return showToast({ type: 'warning', message: 'Harap Mengisi Semua Field!' });
    }

    if (!selectedCar.id) {
      return showToast({ type: 'warning', message: 'Harap Memilih Mobil!' });
    }

    delete orderData.order_detail.booking_zones;

    const utcDifference = moment
      .tz(`${orderData.order_detail.start_booking_date}`, orderData.order_detail.loc_time_id)
      .format('Z')
      .split(':')[0];

    const endBookingDate =
      summaryData.over_time_hour > 0 &&
      orderData.order_detail.start_booking_time == '00:00' &&
      orderData.order_detail.end_booking_time.split(':')[1] === '30'
        ? format(subDays(parseISO(orderData.order_detail.end_booking_date), 1), 'yyyy-MM-dd')
        : orderData.order_detail.end_booking_date;

    const endBookingTime =
      summaryData?.over_time_hour > 0
        ? orderData.order_detail.start_booking_time.split(':')[1] === '30' &&
          orderData.order_detail.end_booking_time.split(':')[1] === '00'
          ? `${orderData.order_detail.start_booking_time.split(':')[0]}:00`
          : orderData.order_detail.start_booking_time.split(':')[1] === '00' &&
            orderData.order_detail.end_booking_time.split(':')[1] === '30'
          ? `${(
              '0' +
              (orderData.order_detail.start_booking_time.split(':')[0] - 1 < 0
                ? 23
                : orderData.order_detail.start_booking_time.split(':')[0] - 1)
            ).slice(-2)}:30`
          : orderData.order_detail.start_booking_time
        : orderData.order_detail.end_booking_time;

    const convertStartBookingTimeUTC = subHours(
      parseISO(`${orderData.order_detail.start_booking_date} ${orderData.order_detail.start_booking_time}`),
      parseInt(utcDifference),
    );

    const convertEndBookingTimeUTC = subHours(parseISO(`${endBookingDate} ${endBookingTime}`), parseInt(utcDifference));

    const priceDifference = orderData.total_amount_order - summaryData.total_payment;

    let mappingAdditionalItem = [];

    if (selectedAdditionalItem?.length > 0) {
      for (let additionalChild of selectedAdditionalItem) {
        const additionalObj = {
          ...additionalChild.additional,
          varieties: [
            {
              ...additionalChild.additional.varieties[0],
              quantity: additionalChild.total,
            },
          ],
        };

        mappingAdditionalItem.push(additionalObj);
      }
    }

    const payload = {
      reference_id: isDriverDeleteApproval ? orderData.id + '' : orderData.id,
      reference_type: 'order',
      approval_setting_id: approvalData.data[0].id,
      data: {
        ...orderData,
        disbursement: {
          ...orderData.disbursement,
          payment_method_id: 0,
          ppn_internal_percentage: 0,
          disbursement_confirmation_image: '',
          payment_platform_fee_type_fixed: 0,
          total_amount_customer_service_fee:
            orderData.total_amount_order === summaryData.total_payment
              ? orderData.disbursement.total_amount_customer_service_fee
              : 0,
          total_amount_external_payment_fee:
            orderData.total_amount_order === summaryData.total_payment
              ? orderData.disbursement.total_amount_external_payment_fee
              : 0,
          payment_platform_fee_type_percentage: 0,
          total_amount_with_customer_service_fee:
            orderData.total_amount_order === summaryData.total_payment || priceDifference > 0
              ? orderData.disbursement.total_amount_with_customer_service_fee
              : 0,
          total_net_amount:
            orderData.total_amount_order === summaryData.total_payment ? orderData.disbursement.total_net_amount : 0,
        },
        booking_price: summaryData.booking_price,
        deposit: summaryData.deposit,
        price_per_day: summaryData.price_per_day,
        total_payment:
          orderData.total_amount_order === summaryData.total_payment
            ? getTotalPrice(orderData?.total_payment, orderData?.point, orderData?.vouchers)
            : getTotalPrice(summaryData?.total_payment, orderData?.point, orderData?.vouchers),
        deposit_e_toll: summaryData.deposit_e_toll,
        outside_operational_charge: summaryData.outside_operational_charge,
        one_day_order_charge: summaryData.one_day_order_charge,
        exceed_passenger_price: summaryData.exced_max_passenger_charge,
        over_time_price: summaryData.over_time_price,
        over_time: summaryData.over_time_hour,
        rental_delivery_fee: summaryData.rental_delivery_fee,
        rental_return_fee: summaryData.rental_return_fee,
        remainder: orderData.type === 'FULL' ? 0 : summaryData.remainder,
        down_payment: orderData.type === 'FULL' ? 0 : summaryData.total_dp,
        total_amount_order:
          orderData.total_amount_order === summaryData.total_payment
            ? orderData.total_amount_order
            : summaryData.total_payment,
        total_diff_price_with_approval: 0,
        order_detail: {
          ...orderData.order_detail,
          baggage: baggage,
          start_booking_date: format(convertStartBookingTimeUTC, 'yyyy-MM-dd'),
          start_booking_time: format(convertStartBookingTimeUTC, 'HH:mm'),
          end_booking_date: format(convertEndBookingTimeUTC, 'yyyy-MM-dd'),
          end_booking_time: format(convertEndBookingTimeUTC, 'HH:mm'),
          vehicle_id: selectedCar.id,
          rental_return_location_detail: returnLocationDetail,
          rental_delivery_location_detail: deliveryLocationDetail,
          rental_delivery_location: deliveryLocation.name,
          rental_return_location: returnLocation.name,
          flight_number: flightNumber,
          landing_time: orderData.order_detail.landing_time,
          vehicle: selectedCar,
        },
        order_additional_fees: additionalList,
        order_violations: violationList,
        addon_total_price: summaryData.addon_total_price,
        addons: isImprovUpdateDataEnabled ? summaryData?.addons : mappingAdditionalItem,
      },
    };
    setShowConfirmation({
      message: 'Apakah anda yakin ingin melanjutkan proses recalculate?',
      show: true,
      onClick: async () => {
        try {
          trackEvent('approval_decided', {
            order_id: orderData?.transaction_key,
            decision: 'recalculate',
            source: 'detail_order_without_driver_edit',
          });
          await dispatch(updateApprovalOrder(payload)).unwrap();
          showToast({ type: 'success', message: 'Approval recalculate berhasil dilakukan' });
          navigate(-1);
        } catch (err) {
          showToast({ type: 'error', message: 'Approval recalculate gagal dilakukan' });
        }
      },
    });
  };

  useEffect(() => {
    if (!checkEmptyObject(data)) {
      const getDeliveryLocation = dataDeliveryLocation?.shuttle?.find(
        (item) => item.name === data.order_detail.rental_delivery_location,
      );
      const getReturnLocation = dataDeliveryLocation?.shuttle?.find(
        (item) => item.name === data.order_detail.rental_return_location,
      );
      setDeliveryLocation(getDeliveryLocation ? getDeliveryLocation : { id: 0, name: '' });
      setReturnLocation(getReturnLocation ? getReturnLocation : { id: 0, name: '' });
      setDeliveryLocationDetail(data.order_detail?.rental_delivery_location_detail || '');
      setReturnLocationDetail(data.order_detail?.rental_return_location_detail || '');
      setFlightNumber(data.order_detail?.flight_number || '');
      setStartRentDate(parseISO(data.order_detail.start_booking_date));
      setEndRentDate(parseISO(data.order_detail.end_booking_date));
      setSelectedOvertime(overtimeWithoutDriver.find((item) => item.value === data.over_time)?.id);
      setBaggage(data.order_detail?.baggage);
      setOrderData({
        ...data,
        order_detail: {
          ...data.order_detail,
          start_booking_time: data.order_detail.start_booking_time.slice(0, 5),
          end_booking_time: data.order_detail.end_booking_time.slice(0, 5),
        },
      });
    }
  }, [data, dataDeliveryLocation]);

  const getAddonsData = (addonsId) => additionalData?.data?.find((item) => item.id === addonsId);

  useEffect(() => {
    if (!orderAddons?.length) return;
    if (checkEmptyObject(additionalData)) return;
    const mappedAddons = orderAddons.map((item) => ({
      additional: getAddonsData(item.id) === undefined ? item : getAddonsData(item.id),
      total: item.varieties[0].quantity,
    }));
    setSelectedAdditionalItem(mappedAddons);
  }, [orderAddons, additionalData]);

  useEffect(() => {
    if (!rentalServicesData.length) return;
    if (checkEmptyObject(data) || !isUpdate) return;
    const subServiceWithoutDriver = rentalServicesData
      ?.find((item) => item.name === 'Sewa Mobil')
      ?.sub_services?.find((item) => item.name === 'Daily')
      ?.facilities?.find((item) => item.name === 'Without Driver');

    dispatch(
      getVehiclesByFilter({
        subServiceId: subServiceWithoutDriver?.id,
        order_id: orderData?.transaction_key,
        limit: 0,
        supportDriver: false,
        customOrder: true,
        startTrip: `${format(
          parseISO(
            orderData?.order_detail?.start_booking_date
              ? orderData.order_detail.start_booking_date
              : data.order_detail.start_booking_date,
          ),
          'yyyy-MM-dd',
        )} ${
          orderData?.order_detail?.start_booking_time
            ? orderData.order_detail.start_booking_time
            : data.order_detail.start_booking_time.slice(0, 5)
        }`,
        endTrip: `${format(
          parseISO(
            orderData?.order_detail?.end_booking_date
              ? orderData.order_detail.end_booking_date
              : data.order_detail.end_booking_date,
          ),
          'yyyy-MM-dd',
        )} ${
          orderData?.order_detail?.end_booking_time
            ? orderData.order_detail.end_booking_time
            : data.order_detail.end_booking_time.slice(0, 5)
        }`,
        locationRental: orderData?.order_detail?.location_id
          ? orderData.order_detail.location_id
          : data.order_detail.location_id,
      }),
    );
    dispatch(
      getAllAddonByParams({
        locationId: orderData?.order_detail?.location_id
          ? orderData.order_detail.location_id
          : data.order_detail.location_id,
        locTimeId: orderData?.order_detail?.loc_time_id
          ? orderData.order_detail.loc_time_id
          : data.order_detail.loc_time_id,
        startDate: orderData.order_detail.start_booking_date,
        endDate: orderData.order_detail.end_booking_date,
        startTime: orderData.order_detail.start_booking_time + ':00',
        endTime: orderData.order_detail.end_booking_time + ':00',
        limit: 0,
      }),
    );
  }, [data, isUpdate, orderData, rentalServicesData]);

  useEffect(() => {
    if (!isPageLoaded) return;
    if (
      orderData.order_detail.end_booking_date === '' ||
      orderData.order_detail.end_booking_time === '' ||
      orderData.order_detail.start_booking_date === '' ||
      orderData.order_detail.start_booking_time === '' ||
      !rentalServicesData.length ||
      !selectedCar?.id ||
      !deliveryLocation?.id ||
      !returnLocation?.id
    )
      return;
    const subServiceWithoutDriver = rentalServicesData
      ?.find((item) => item.name === 'Sewa Mobil')
      ?.sub_services?.find((item) => item.name === 'Daily')
      ?.facilities?.find((item) => item.name === 'Without Driver');

    let mappingAdditionalItem = [];

    if (selectedAdditionalItem?.length > 0) {
      for (let additionalChild of selectedAdditionalItem) {
        const additionalObj = {
          id: additionalChild.additional.id,
          varieties: [
            {
              id: additionalChild.additional.varieties[0].id,
              quantity: additionalChild.total,
            },
          ],
        };

        mappingAdditionalItem.push(additionalObj);
      }
    }

    dispatch(
      fetchSummaryOrder({
        end_booking_date: orderData?.order_detail.end_booking_date,
        end_booking_time: orderData?.order_detail.end_booking_time,
        start_booking_date: orderData?.order_detail.start_booking_date,
        start_booking_time: orderData?.order_detail.start_booking_time,
        deposit: orderData?.deposit,
        deposit_e_toll: orderData?.deposit_e_toll,
        order_type_id: 1,
        vehicle_id: selectedCar?.id,
        without_driver: 1,
        rental_return_id: returnLocation.id,
        rental_delivery_id: deliveryLocation.id,
        promotion_id: selectedCar?.promo?.promo_id,
        pasengger_number: orderData?.order_detail.passenger_number,
        vehicle_category_id: selectedCar.category?.id,
        location_id: orderData?.order_detail.location_id,
        point: orderData?.point,
        sub_services_id: subServiceWithoutDriver?.id,
        overtime: overtimeWithoutDriver.find((item) => item.id === selectedOvertime)?.value,
        order_additional_fees: additionalList,
        violations: violationPrice,
        order_id: orderData.id,
        addons: mappingAdditionalItem,
      }),
    );
  }, [
    orderData,
    selectedCar,
    rentalServicesData,
    deliveryLocation,
    rentalLocation,
    selectedOvertime,
    additionalList,
    violationPrice,
    isPageLoaded,
    selectedAdditionalItem,
  ]);

  const countOvertime = (startTime, endTime) => {
    const startHour = +startTime.split(':')[0];
    const startMinute = +startTime.split(':')[1];
    const endHour = +endTime.split(':')[0];
    const endMinute = +endTime.split(':')[1];

    let overtimeValue = 0;

    if (startHour === endHour) {
      if (startMinute === endMinute) {
        overtimeValue = 0;
      }

      if (startMinute < endMinute) {
        overtimeValue = 1;
      }
    }

    if (startHour < endHour) {
      let newOvertime = endHour - startHour;

      if (startMinute < endMinute) {
        newOvertime += 1;
      }

      overtimeValue = newOvertime;
    }

    return overtimeValue;
  };

  return (
    <div className="detail-order">
      <div className="detail-order__back-btn" onClick={backHandler}>
        <LeftArrow />
        <p>Kembali</p>
      </div>
      <TableWrapper icon={<CarIcon fill="#009EF7" width="25px" height="25px" />} title="Lepas Kunci">
        <div className="detail-order__renter-detail">
          <div className="detail-order__renter-detail-title">
            <h1>Detail Penyewa</h1>
          </div>
          <div className="detail-order__renter-detail-body">
            <InputField label="Nama Lengkap" disabled value={data?.user_name || '-'} />
            <InputField label="Email" disabled value={data?.email || '-'} />

            <div className="detail-order__renter-detail-body__phone-number">
              <InputField
                label="No Handphone"
                disabled
                value={data?.is_admin_creation ? data?.phone_country_code : customer?.phone_code || '-'}
                icon={
                  <img
                    src={`https://flagcdn.com/w2560/${selectedCountry?.code?.toLowerCase()}.png`}
                    width={26}
                    height={16}
                    alt={selectedCountry?.name + ' nation flag'}
                  />
                }
              />
              <InputField value={data?.phone_number || '-'} disabled />
            </div>

            <InputField label="Negara" disabled value={selectedCountry?.name || '-'} />

            <div className="detail-order__renter-detail-body__phone-number">
              <div className="detail-order__renter-detail-body__phone-number-wrapper">
                <InputField
                  label="Whatsapp"
                  disabled
                  value={data?.is_admin_creation ? data?.phone_country_code : customer?.phone_code || '-'}
                  icon={
                    <img
                      src={`https://flagcdn.com/w2560/${selectedCountry?.code?.toLowerCase()}.png`}
                      width={26}
                      height={16}
                      alt={selectedCountry?.name + ' nation flag'}
                    />
                  }
                />
                <div className="detail-order__renter-detail-body__image-wrapper"></div>
              </div>
              <InputField disabled value={data?.wa_number || '-'} />
            </div>
          </div>
        </div>

        <div className="detail-order__rent-detail">
          <div className="detail-order__rent-detail-title">
            <h1>Detail Sewa</h1>
          </div>
          <div className="detail-order__rent-detail-body">
            <SelectFieldShuttle
              label="Lokasi Pengantaran"
              htmlFor="lokasi-pengantaran"
              value={deliveryLocation.id}
              data={dataDeliveryLocation.shuttle}
              onChange={(item) => {
                setDeliveryLocation(item);
                setOrderData({
                  ...orderData,
                  rental_delivery_fee: item.fee,
                  order_detail: {
                    ...orderData.order_detail,
                    rental_delivery_location: item.name,
                    location_id: item.location_id,
                    ...(isImprovUpdateDataEnabled &&
                      !item.airport && {
                        landing_time: '',
                        flight_number: '',
                      }),
                  },
                });
                if (isImprovUpdateDataEnabled && !item.airport) {
                  setFlightNumber('');
                }
              }}
              placeholder="Masukan Lokasi Pengantaran"
            />
            <InputField
              label="Detail Lokasi Pengantaran"
              value={deliveryLocationDetail}
              onChange={(e) => setDeliveryLocationDetail(e.target.value)}
            />

            <SelectFieldShuttle
              label="Lokasi Pengembalian"
              htmlFor="lokasi-pengembalian"
              value={returnLocation.id}
              data={dataDeliveryLocation.shuttle}
              onChange={(item) => {
                setReturnLocation(item);
                setOrderData({
                  ...orderData,
                  rental_return_fee: item.fee,
                  order_detail: {
                    ...orderData.order_detail,
                    rental_return_location: item.name,
                    location_id: item.location_id,
                  },
                });
              }}
              placeholder="Masukan Lokasi Pengembalian"
            />
            <InputField
              label="Detail Lokasi Pengambilan"
              value={returnLocationDetail}
              onChange={(e) => setReturnLocationDetail(e.target.value)}
            />

            {orderData?.order_detail?.flight_number || deliveryLocation?.airport || returnLocation?.airport ? (
              <InputField
                label="Nomor Penerbangan"
                value={flightNumber}
                onChange={(e) => setFlightNumber(e.target.value)}
              />
            ) : null}
            {orderData?.order_detail?.landing_time || deliveryLocation?.airport || returnLocation?.airport ? (
              <SelectFieldTime
                label="Jam Landing"
                htmlFor="jam-landing"
                placeholder="00:00"
                value={orderData.order_detail.landing_time}
                onChange={(hour, minute) => {
                  setOrderData({
                    ...orderData,
                    order_detail: { ...orderData.order_detail, landing_time: `${hour}:${minute ? minute : '00'}` },
                  });
                }}
                showAllHours={true}
              />
            ) : null}

            <InputField label="Tipe Sewa" disabled value="Harian" />
            <SelectFieldDropdown
              label="Tambahan Overtime"
              value={selectedOvertime}
              data={overtimeWithoutDriver}
              placeholder="Overtime"
              disable={true}
            />

            <div className="detail-order__rent-detail-body__rent-date">
              <SelectFieldDate
                label="Tanggal Mulai"
                htmlFor="tanggal-mulai"
                name="tanggal-mulai"
                handleDaySelect={(date) => {
                  if (date) {
                    setStartRentDate(date);
                    setEndRentDate('');
                    setOrderData({
                      ...orderData,
                      order_detail: {
                        ...orderData.order_detail,
                        start_booking_date: format(date, 'yyyy-MM-dd').toString(),
                        vehicle_id: '',
                      },
                    });
                    setSelectedCar(undefined);
                    setVehicleInput('');
                  }
                }}
                selectedDay={startRentDate}
                value={startRentDate !== '' ? format(startRentDate, 'dd MMMM yyyy') : startRentDate}
                placeholder="Pilih Tanggal"
              />
              <SelectFieldTime
                label="Jam Mulai"
                htmlFor="jam-mulai"
                placeholder="00:00"
                value={orderData.order_detail.start_booking_time}
                onChange={(hour, minute) => {
                  setOrderData({
                    ...orderData,
                    order_detail: {
                      ...orderData.order_detail,
                      start_booking_time: `${hour}:${minute ? minute : '00'}`,
                      end_booking_time: `${hour}:${minute ? minute : '00'}`,
                      vehicle_id: '',
                    },
                  });
                  setSelectedOvertime('ot0');
                  setSelectedCar(undefined);
                  setVehicleInput('');
                }}
                disable={startRentDate === '' ? true : false}
                showAllHours={true}
              />
            </div>

            <div className="detail-order__rent-detail-body__rent-date">
              <SelectFieldDate
                label="Tanggal Selesai"
                htmlFor="tanggal-selesai"
                name="tanggal-selesai"
                handleDaySelect={(date) => {
                  if (date) {
                    setEndRentDate(date);
                    setOrderData({
                      ...orderData,
                      order_detail: {
                        ...orderData.order_detail,
                        end_booking_date: format(date, 'yyyy-MM-dd').toString(),
                        vehicle_id: '',
                      },
                    });
                    setSelectedCar(undefined);
                    setVehicleInput('');
                  }
                }}
                selectedDay={endRentDate}
                fromDate={addDays(startRentDate === '' ? parseISO(startRentDate) : startRentDate, 1)}
                value={endRentDate !== '' ? format(endRentDate, 'dd MMMM yyyy') : endRentDate}
                placeholder="Pilih Tanggal"
                disable={startRentDate === '' ? true : false}
              />
              <SelectFieldTime
                label="Jam Selesai"
                htmlFor="jam-selesai"
                placeholder="00:00"
                value={orderData.order_detail.end_booking_time}
                onChange={(hour, minute) => {
                  const overtimeValue = countOvertime(
                    orderData.order_detail.start_booking_time,
                    `${hour}:${minute ? minute : '00'}`,
                  );
                  setOrderData({
                    ...orderData,
                    order_detail: { ...orderData.order_detail, end_booking_time: `${hour}:${minute ? minute : '00'}` },
                    vehicle_id: '',
                  });
                  setSelectedCar(undefined);
                  setVehicleInput('');
                  setSelectedOvertime(
                    overtimeValue > 0 ? overtimeWithoutDriver.find((item) => item.value === overtimeValue)?.id : 'ot0',
                  );
                }}
                disable={startRentDate === '' ? true : false}
                startHour={orderData.order_detail.start_booking_time}
                showAllHours={true}
              />
            </div>

            <SelectFieldCar
              label="Jenis Mobil"
              htmlFor="pilih-mobil"
              placeholder="Cari Mobil.."
              data={vehicleList.vehicles}
              selectedCar={selectedCar}
              onSelectCar={setSelectedCar}
              value={vehicleInput}
              onChange={(e) => setVehicleInput(e.target.value)}
              onClick={(vehicleId, resetVehicle) => {
                if (vehicleId === '') {
                  setVehicleInput(resetVehicle);
                  return;
                }
                setVehicleInput(resetVehicle);
              }}
              payloadFilter={{
                locationRental: orderData?.order_detail?.location_id,
                startTrip: `${orderData?.order_detail?.start_booking_date} ${orderData?.order_detail?.start_booking_time}`,
                endTrip: `${orderData?.order_detail?.end_booking_date} ${orderData?.order_detail?.end_booking_time}`,
                supportDriver: false,
                customOrder: true,
                page: 0,
                limit: 0,
                order_id: orderData.transaction_key,
                subServiceId: rentalServicesData
                  ?.find((item) => item.name === 'Sewa Mobil')
                  ?.sub_services?.find((item) => item.name === 'Daily')
                  ?.facilities?.find((item) => item.name === 'Without Driver')?.id,
              }}
            />
            <SelectFieldDropdown
              label="Jumlah Penumpang"
              htmlFor="pilih-jumlah-penumpang"
              placeholder="Pilih Jumlah Penumpang"
              icon={<CarChair />}
              data={
                selectedCar
                  ? Array.from({ length: selectedCar?.max_passanger + 1 }, (_, idx) => idx + 1).map((item) => ({
                      id: item,
                      name: `${item} Orang`,
                    }))
                  : []
              }
              value={orderData.order_detail.passenger_number ?? ''}
              onChange={(e) =>
                setOrderData({
                  ...orderData,
                  order_detail: { ...orderData.order_detail, passenger_number: +e.target.id },
                })
              }
              disable={!selectedCar}
            />

            <div className="detail-order__rent-detail-body__rent-date">
              <InputField label="Kapasitas Koper" disabled value={selectedCar?.max_suitcase ?? '-'} />
              <InputField
                label="Koper yang dibawa"
                value={baggage ?? ''}
                onChange={(e) => setBaggage(isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value))}
              />
            </div>

            {isImprovUpdateDataEnabled && orderData?.customer_name_meet_and_greet ? (
              <InputField
                label="Meet and Greet Board"
                value={orderData.customer_name_meet_and_greet || '-'}
                onChange={(e) => setOrderData({ ...orderData, customer_name_meet_and_greet: e.target.value })}
              />
            ) : null}
          </div>
        </div>

        <div className="detail-order__price-detail">
          <div className="detail-order__price-detail-title">
            <h1>Rincian Biaya</h1>
          </div>
          <div className="detail-order__price-detail-body">
            <InputField
              label="Biaya Sewa Mobil"
              disabled
              value={setCurrency(summaryData?.booking_price, orderData?.currency) || '-'}
            />
            <InputField
              label="Deposit"
              disabled
              value={setCurrency(summaryData?.deposit, orderData?.currency) || '-'}
            />
            <InputField
              label="Deposit e-Toll"
              disabled
              value={setCurrency(summaryData?.deposit_e_toll, orderData?.currency) || '-'}
            />
            <InputField
              label="Overtime"
              disabled
              value={setCurrency(summaryData?.over_time_price, orderData?.currency) || '-'}
            />
            <InputField
              label="One Day Charge"
              disabled
              value={setCurrency(summaryData?.one_day_order_charge, orderData?.currency) || '-'}
            />
            <InputField
              label="Outside Operational Hour Charge"
              disabled
              value={setCurrency(summaryData?.outside_operational_charge, orderData?.currency) || '-'}
            />
            <InputField
              label="Exceed Max Passenger Charge"
              disabled
              value={setCurrency(summaryData?.exced_max_passenger_charge, orderData?.currency) || '-'}
            />
            <InputField
              label="Biaya Pengantaran"
              disabled
              value={setCurrency(summaryData?.rental_delivery_fee, orderData?.currency) || '-'}
            />
            <InputField
              label="Biaya Pengembalian"
              disabled
              value={setCurrency(summaryData?.rental_return_fee, orderData?.currency) || '-'}
            />
            <InputField
              label="Holiday Charge"
              disabled
              value={setCurrency(summaryData?.public_holiday_charge, orderData?.currency) || '-'}
            />
            <InputField
              label="Pembayaran Dengan Point"
              disabled
              value={summaryData?.point}
              icon={<PointsIcon />}
              iconPosition="start"
            />
            <InputField
              label="Tipe Pembayaran"
              disabled
              value={
                orderData.type === 'HALF'
                  ? `Pembayaran DP ${
                      orderData?.order_detail?.dp_rule?.formula_percentage?.value
                        ? `${orderData.order_detail.dp_rule.formula_percentage.value}%`
                        : ''
                    }`
                  : 'Pembayaran Full'
              }
            />
            {orderData.type === 'HALF' ? (
              <>
                <InputField
                  label={`Biaya Bayar DP ${
                    orderData?.order_detail?.dp_rule?.formula_percentage?.value
                      ? `${orderData.order_detail.dp_rule.formula_percentage.value}%`
                      : ''
                  }`}
                  disabled
                  value={setCurrency(summaryData.total_dp, orderData?.currency) || '-'}
                />
                <InputField
                  label="Biaya selanjutnya yang harus dibayar"
                  disabled
                  value={setCurrency(summaryData.remainder, orderData?.currency) || '-'}
                />
              </>
            ) : null}

            {isImprovUpdateDataEnabled && orderData?.fee_meet_greet_board ? (
              <InputField
                label="Biaya Meet and Greet Board"
                disabled
                value={orderData?.fee_meet_greet_board ? setCurrency(orderData.fee_meet_greet_board) : '-'}
              />
            ) : null}
          </div>
        </div>

        <div className="detail-order__additional-item">
          <h2>Additional</h2>
          <div className="wrapper">
            <SelectFieldAdditionalItem
              label="Pilih Tambahan"
              id="tambahan"
              className="additional"
              placeholder="Cari Perlengkapan Khusus.."
              selectedAdditionalItem={selectedAdditionalItem}
              setSelectedAdditionalItem={setSelectedAdditionalItem}
              value={additionalSearchInput}
              onChange={(e) => setAdditionalSearchInput(e.target.value)}
              data={additionalData?.data || []}
              disable={!additionalData || checkEmptyObject(additionalData)}
              filterParams={{
                locationRental: orderData?.order_detail?.location_id,
                locTimeId: orderData?.order_detail?.loc_time_id,
                startDate: orderData?.order_detail?.start_booking_date,
                endDate: orderData?.order_detail?.end_booking_date,
                startTime: orderData?.order_detail?.start_booking_time + ':00',
                endTime: orderData?.order_detail?.end_booking_time + ':00',
              }}
            />

            <InputField
              label="Total Biaya Permintaan Khusus"
              placeholder="Rp 0"
              value={
                summaryData?.addon_total_price ? setCurrency(summaryData.addon_total_price, orderData?.currency) : ''
              }
              disable
            />

            {/* <TextAreaInput
            label="Detail Tambahan"
            htmlFor="detail-tambahan"
            placeholder="Tulis Keterangan"
            value={descriptionAdditionalItem}
            onChange={(e) => setDescriptionAdditionalItem(e.target.value)}
            disable={isOrderConfirmation}
          /> */}
          </div>
        </div>

        <div className="detail-order__price-detail-body__input-violation">
          <InputViolation
            violationList={violationList}
            setViolationList={setViolationList}
            totalPrice={violationPrice}
            isOnEdit={true}
            data={[]}
            violationPrice={0}
          />
        </div>

        <div className="detail-order__price-detail-body__input-additional">
          <InputAdditionals
            additionalList={additionalList}
            setAdditionalList={setAdditionalList}
            totalPrice={additionalPrice}
            isOnEdit={true}
            data={[]}
            additionalPrice={0}
          />
        </div>

        <div className="detail-order__total-price">
          <div className="total-price-wrapper">
            <h3>Total</h3>
            <p>
              {setCurrency(
                getTotalPrice(summaryData?.total_payment, orderData?.point, orderData?.vouchers),
                orderData?.currency,
              )}
            </p>
          </div>
        </div>

        <div className="detail-order__transaction-buttons">
          <Button variant="outline" className="button btn-cancel" width={208} size="sm" onClick={backHandler}>
            Batal
          </Button>

          {checkPermission(offCanvasMenu, currentMenu, 'update') && (
            <Button className="button" width={208} size="sm" onClick={updateHandler}>
              Recalculate
            </Button>
          )}
        </div>
      </TableWrapper>
    </div>
  );
};

export default EditOrder;
